<template>
  <div v-if="loaded">
    <DxLookup
      id="companyAddressLookup"
      :data-source="ds"
      :grouped="enabled"
      width="500"
      display-expr="name"
      value-expr="lookupKey"
      :value="selectedLookupKey"
      :search-expr="
        enabled
          ? ['companyName', 'name']
          : ['companyName', 'addressName', 'companyCode']
      "
      styling-mode="filled"
      field-template="field-item"
      item-template="list-item"
      :page-load-mode="'scrollBottom'"
      :next-button-text="enabled ? null : $t('alert.viewMore')"
      @item-click="itemClick"
    >
      <template #field-item>
        <div class="flex">
          <div class="bold txt-ellipsis">
            {{ companyName }}
          </div>
          <div class="italic txt-ellipsis">
            - {{ fixEncoding(currentAddress) }}
          </div>
        </div>
      </template>
      <template #list-item="{ data }">
        <div v-if="data.name.length > 0">
          <span v-if="!enabled">
            <div>
              <strong class="fourtyChars">
                {{ data.companyCode }} - {{ data.name }}
              </strong>
            </div>

            <span class="fourtyChars">
              {{ data.addressName }}
            </span>
          </span>

          <div v-if="enabled">{{ data.name }}</div>
        </div>
      </template>
      <DxDropDownOptions
        height="500"
        :shading="true"
        :close-on-outside-click="true"
        :show-title="false"
        :wrapper-attr="{ class: 'client-menu-lookup-popup' }"
      />
    </DxLookup>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import EventBus from '@/services/event-bus';
import { DxLookup, DxDropDownOptions } from 'devextreme-vue/lookup';
import { locale, loadMessages } from 'devextreme/localization';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { ALL_LANGUAGES } from '@/js/constants';

import esMessages from '@/json/langs/es.json';
import frMessages from '@/json/langs/fr.json';
import ptMessages from '@/json/langs/pt.json';
import enMessages from '@/json/langs/en.json';
import itMessages from '@/json/langs/it.json';

export default {
  components: {
    DxLookup,
    DxDropDownOptions,
  },
  data() {
    return {
      loaded: false,
      // ds: null,
      langsDx: {
        esMessages,
        frMessages,
        ptMessages,
        enMessages,
        itMessages,
      },
      cleanItemsCached: [],
      dsInstance: null,
    };
  },
  computed: {
    ds() {
      return this.dsInstance;
    },
    ...mapState('clientMenu', ['currentAddress']),
    ...mapGetters('clientMenu', ['items']),
    ...mapState('contact', [
      'enabled',
      'companyName',
      'hasAccessToAdvanceService',
      'address',
      'company',
      'addressName',
    ]),
    ...mapGetters('user', ['userProfile']),
    ...mapState('user', ['languageInterface']),
    ...mapGetters('contact', ['contactInfo']),
    ...mapState('samplesRegisterModule', ['idInvoice']),
    selectedLookupKey() {
      if (
        !Array.isArray(this.cleanItemsCached) ||
        this.cleanItemsCached.length === 0
      ) {
        return null;
      }

      const norm = (v) => {
        return String(v ?? '').trim();
      };

      const match = this.cleanItemsCached.find((item) => {
        const sameCodes =
          norm(item.addressCode) === norm(this.address) &&
          norm(
            this.enabled ? item.addressCompany : item.companyCode
          ) === norm(this.company);

        const sameNames =
          norm(item.addressName) === norm(this.addressName) &&
          norm(item.companyName) === norm(this.companyName);

        return sameCodes || sameNames;
      });

      return match
        ? match.lookupKey
        : this.cleanItemsCached[0].lookupKey;
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        if (!Array.isArray(newItems)) {
          this.cleanItemsCached = [];
          this.buildDataSource();
          return;
        }

        this.cleanItemsCached = newItems.map((item) => ({
          ...item,
          lookupKey: this.getLookupKey(item),
          name: this.fixEncoding(item.name),
          companyName: this.fixEncoding(item.companyName),
          addressName: this.fixEncoding(item.addressName),
        }));
        this.buildDataSource();

        // Avisamos al contenedor cuando ya ha cargado los clientes, para evitar 
        // desajustes visuales de carga.
        if (this.loaded && newItems.length > 0) {
          this.$emit('ready');
        }
      },
    },
    enabled: {
      immediate: true,
      handler() {
        this.buildDataSource();
      },
    },
  },
  async beforeMount() {
    this.loaded = false;

    if (this.enabled) {
      if (this.items.length !== 0) {
        this.setAddress(this.items[0].addressCode);
        this.setCompany(this.items[0].addressCompany);
        this.setAddressName(this.items[0].name);
        this.setCompanyName(this.items[0].companyName);
        this.setCurrency(this.items[0].currency || null);

        this.setMenuAddress();

        this.items.forEach((contact) => {
          if (contact.id === this.items[0].id) {
            this.setAddresses(contact.itemsData);
          }
        });
      }
    } else if (this.items.length !== 0) {
      this.setAddress(this.items[0].addressCode);
      this.setAddressName(this.items[0].addressName);
      this.setCompany(this.items[0].companyCode);
      this.setCompanyName(this.items[0].companyName);
      this.setCurrency(this.items[0].currency || null);

      this.setMenuAddress();
    }

    // El selector ya puede mostrarse: las comprobaciones auxiliares no deben
    // retrasar la disponibilidad del cliente seleccionado.
    this.loaded = true;
    if (this.items.length > 0) {
      this.$emit('ready');
    }

    try {
      if (this.items.length !== 0) {
        await this.checkAccessToAdvanceService();
        await this.checkAccessToNutritionalMonitoring();
      }
    } catch {
      // handle errror
    }
  },
  mounted() {
    EventBus.$emit('onChangeCompany', true);
  },
  methods: {
    // DevExtreme necesita una clave estable por fila para no marcar varias opciones
    // como seleccionadas cuando algunos items no traen `id` desde origen.
    getLookupKey(item) {
      const explicitId = String(item?.id ?? '').trim();

      if (explicitId) {
        return explicitId;
      }

      const companyKey = this.enabled
        ? item?.addressCompany
        : item?.companyCode;
      const addressLabel = this.enabled
        ? item?.name
        : item?.addressName;

      return [
        companyKey,
        item?.addressCode,
        item?.companyName,
        addressLabel,
      ]
        .map((value) => String(value ?? '').trim())
        .join('||');
    },
    buildDataSource() {
      const store = new ArrayStore({
        // Keep store key aligned with valueExpr to avoid multiple visual selections
        key: 'lookupKey',
        data: this.cleanItemsCached,
      });

      this.dsInstance = new DataSource({
        store,
        paginate: true,
        pageSize: 100,
        group: this.enabled ? 'companyName' : undefined,
      });
    },
    fixEncoding(str) {
      if (typeof str !== 'string') return str;

      // detecta UTF-8 mal decodificado típico
      if (!/Ã.|Â.|�/.test(str)) return str;

      try {
        return decodeURIComponent(escape(str));
      } catch {
        return str;
      }
    },
    async itemClick(e) {
      if (this.enabled) {
        if (e.itemData.addressCode) {
          this.setAddressName(e.itemData.name);
          this.setAddress(e.itemData.addressCode);
          this.setCompany(e.itemData.addressCompany);
          this.setCompanyName(e.itemData.companyName);
          this.setCurrency(e.itemData.currency || null);
          this.setContactIndex(e.itemData.contactIndex);
          this.setMenuAddress();

          this.items.forEach((company) => {
            if (company.id === e.itemData.id) {
              this.setAddresses(company.itemsData);
            }
          });
        }
      } else {
        this.setAddressName(e.itemData.addressName);
        this.setAddress(e.itemData.addressCode);
        this.setCompanyName(e.itemData.companyName);
        this.setCompany(e.itemData.companyCode);
        this.setCurrency(e.itemData.currency || null);
        this.setMenuAddress();
      }

      try {
        await this.checkAccessToAdvanceService();
        await this.checkAccessToNutritionalMonitoring();
      } catch {
        // handle errror
      }

      if (this.enabled) {
        this.clearSamplesFilters();
        setTimeout(() => {
          const lang = this.getPreferredLanguage(
            this.userProfile.language
          );

          this.$i18n.i18next.changeLanguage(lang);
          this.updateLanguageInterface(lang);

          locale(`${lang}-${lang.toUpperCase()}`);
          loadMessages(this.langsDx[`${lang}Messages`]);
        }, 0);
      }

      const currentRouteName =
        this.$f7.views.main.router.currentRoute.name;
      const currentRoutePath =
        this.$f7.views.main.router.currentRoute.path;

      if (currentRouteName || currentRoutePath) {
        if (currentRouteName.toLowerCase().includes('detail')) {
          const previousRoute =
            this.$f7.views.main.router.previousRoute.name;
          this.$f7.views.main.router.navigate(
            {
              name: previousRoute,
            },
            {
              animate: true,
              transition: 'f7-fade',
              reloadCurrent: true,
            }
          );
        } else if (
          (currentRoutePath != null &&
            currentRoutePath
              .toLowerCase()
              .includes('/advanced-services')) ||
          currentRoutePath.toLowerCase().includes('/gis')
        ) {
          const routeToGo =
            this.enabled === false ||
            (this.enabled && this.hasAccessToAdvanceService)
              ? 'AdvancedServicesPage'
              : 'Home';

          this.$f7.views.main.router.navigate(
            {
              name: routeToGo,
            },
            {
              animate: true,
              transition: 'f7-fade',
              reloadCurrent: true,
            }
          );
        } else if (
          currentRoutePath != null &&
          currentRoutePath.toLowerCase().includes('/complaint')
        ) {
          // Cada vez que se cambia de empresa, se emite el evento
          // Si la ruta es del apartado Gestión de incidencias, redirigimos a ComplaintManagement para recargar listado
          const routeToGo = 'ComplaintManagement';
          this.$f7.views.main.router.navigate(
            {
              name: routeToGo,
            },
            {
              animate: true,
              transition: 'f7-fade',
              reloadCurrent: true,
            }
          );
        } else if (
          currentRoutePath != null &&
          currentRoutePath.toLowerCase().includes('/samplesregister')
        ) {
          // Cada vez que se cambia de empresa, se emite el evento
          // Si la ruta es del apartado Registro de muestras, redirigimos al listado de albaranes
          // Borramos el albarán actual si cambia de cliente a mitad del proceso
          if (
            this.idInvoice !== null &&
            this.idInvoice !== undefined &&
            this.idInvoice !== '' &&
            !Array.isArray(this.idInvoice)
          ) {
            this.deleteInvoice({
              idInvoice: this.idInvoice,
              deleteSource: 'company_switch_during_registration',
            });
          }
          const routeToGo = 'SamplesList';
          this.$f7.views.main.router.navigate(
            {
              name: routeToGo,
            },
            {
              animate: true,
              transition: 'f7-fade',
              reloadCurrent: true,
            }
          );
        } else if (
          currentRoutePath != null &&
          currentRoutePath.toLowerCase().includes('/client-area')
        ) {
          // Si cambia de cliente y tenía el área de clientes abierta, que redirija al perfil
          const routeToGo = 'ProfilePage';
          this.$f7.views.main.router.navigate(
            {
              name: routeToGo,
            },
            {
              animate: true,
              transition: 'f7-fade',
              reloadCurrent: true,
            }
          );
        } else if (
          currentRoutePath != null &&
          currentRoutePath.toLowerCase().includes('/issued-invoices')
        ) {
          // Cada vez que se cambia de empresa, se emite el evento
          // Si la ruta es del apartado Mis Facturas, redirigimos al Dashboard de inicio
          // Así evitamos que cargue el apartado de facturas un cliente sin acceso
          const routeToGo = 'DashboardHome';
          this.$f7.views.main.router.navigate(
            {
              name: routeToGo,
            },
            {
              animate: true,
              transition: 'f7-fade',
              reloadCurrent: true,
            }
          );
        } else {
          EventBus.$emit(currentRouteName, null);
          if (currentRouteName === 'FoodStatsPage') {
            EventBus.$emit('FoodStatsPageClearFilter', null);
          }
        }
      }
    },
    translateLanguage(value) {
      let key = '';
      for (const lang in ALL_LANGUAGES) {
        if (ALL_LANGUAGES[lang].key === value) {
          key = ALL_LANGUAGES[lang].value;
        }
      }

      return key;
    },
    normalizeLanguage(value) {
      const normalizedValue = String(value || '').trim();
      if (!normalizedValue) return '';

      const byValue = ALL_LANGUAGES.find(
        (lang) => lang.value === normalizedValue.toLowerCase()
      );
      if (byValue) return byValue.value;

      const byKey = ALL_LANGUAGES.find(
        (lang) => lang.key === normalizedValue.toUpperCase()
      );
      return byKey?.value || '';
    },
    getNavigatorLanguage() {
      const navigatorLanguage = navigator.language
        .substring(0, 2)
        .toLowerCase();

      return ALL_LANGUAGES.some(
        (lang) => lang.value === navigatorLanguage
      )
        ? navigatorLanguage
        : 'es';
    },
    getPreferredLanguage(fallbackLanguage) {
      return (
        this.normalizeLanguage(this.languageInterface) ||
        this.normalizeLanguage(this.contactInfo?.language) ||
        this.normalizeLanguage(fallbackLanguage) ||
        this.getNavigatorLanguage()
      );
    },
    ...mapActions('contact', [
      'setAddressName',
      'setAddress',
      'setCompany',
      'setCompanyName',
      'setCurrency',
      'setAddresses',
      'setContactIndex',
      'checkAccessToAdvanceService',
      'checkAccessToNutritionalMonitoring',
    ]),
    ...mapActions('clientMenu', {
      setMenuAddress: 'setAddress',
    }),
    ...mapActions('filter', ['clearSamplesFilters']),
    ...mapActions('user', ['updateLanguageInterface']),
    ...mapActions('samplesRegisterModule', ['deleteInvoice']),
  },
};
</script>
<style scoped lang="scss">
@import './ClientMenu.styles.scss';
</style>
